import { ref, onMounted, computed, getCurrentInstance } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";
import store from "@/store";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import { useTemplateMode } from "../useTemplateMode";

export default function useConfigRemoteOptions(uuid, remoteOptionsData, emit, props) {
  const vm = getCurrentInstance();
  const proxy = vm.root.proxy;
  const toast = useToast();
  const { t } = useI18nUtils();
  const { isTemplateMode, emitTemplateUpdate } = useTemplateMode(props, emit);

  const isLoading = computed(() => props.loading);
  const isSaving = ref(false);
  const activeTab = ref("standard"); // 'standard' or 'entre'
  const standardFormObserver = ref(null);
  const entreFormObserver = ref(null);

  const remoteOptions = ref({
    // General (Standard tab)
    remoteKey: "",
    remoteDisarm: false,

    // Dialer (Standard tab)
    armedAnswerRings: 8,
    disarmedAnswerRings: 8,
    pcModem: false,

    // Network (Standard tab)
    allowNetworkRemote: false,
    encryptNetworkRemote: false,
    networkProgrammingPort: 2001,

    // Cellular (Standard tab)
    allowCellularRemote: false,
    encryptCellRemote: false,
    cellApn1: "",
    cellApn2: "",

    // Entré Connection (shown in Entré tab, controls Standard payload)
    entreConnection: "NONE",
    entreInTcpPort: 2011,
    entreIpAddress: "0.0.0.0",
    entreArmDisarmReports: false,
    entreZoneReports: false,
    entreUserCommandReports: false,
    entreDoorReports: false,
    entreSupervisoryReports: false,

    // Entré Settings (Entré tab)
    entreOutboundTcpPort: 2001,
    entreBackupIpAddress: "0.0.0.0",
    entreBackupTcpPort: 2001,
    entreCheckInMinutes: 0,
    entrePassphrase: "",

    // Program Sync (shown in Standard tab, controls Entré payload)
    sendLocalChanges: "NONE",
    remoteChangeIp: "0.0.0.0",
    remoteChangePort: 2002,
    remotePhoneNumber: "",

    // Shared fields
    alarmReceiver: "N",
    serviceReceiver: "Y",
    appKey: "",
  });

  const yesNoOptions = [
    { value: "N", text: proxy.$t("dmp.remoteOptions.no") },
    { value: "Y", text: proxy.$t("dmp.remoteOptions.yes") },
  ];

  const sendLocalChangesOptions = [
    { value: "NONE", text: proxy.$t("dmp.remoteOptions.none") },
    { value: "NETWORK", text: proxy.$t("dmp.remoteOptions.network") },
    { value: "DD", text: proxy.$t("dmp.remoteOptions.dd") },
  ];

  const entreConnectionOptions = [
    { value: "NONE", text: proxy.$t("dmp.remoteOptions.none") },
    { value: "NETWORK", text: proxy.$t("dmp.remoteOptions.network") },
  ];

  // Computed to determine which fields are enabled
  const isStandardTab = computed(() => activeTab.value === "standard");
  const isEntreTab = computed(() => activeTab.value === "entre");
  const entreConnectionIsNone = computed(() => remoteOptions.value.entreConnection === "NONE");
  const entreConnectionIsNetwork = computed(() => remoteOptions.value.entreConnection === "NETWORK");
  const sendLocalChangesIsNone = computed(() => remoteOptions.value.sendLocalChanges === "NONE");
  const sendLocalChangesIsNetwork = computed(() => remoteOptions.value.sendLocalChanges === "NETWORK");
  const sendLocalChangesIsDD = computed(() => remoteOptions.value.sendLocalChanges === "DD");

  // Computed properties for field enable/disable logic
  // Standard Tab: All standard fields are always enabled
  const standardFieldsEnabled = computed(() => isStandardTab.value);

  // Entré Connection fields: Enabled when entreConnection = NETWORK, regardless of active tab
  const entreConnectionFieldsEnabled = computed(() => entreConnectionIsNetwork.value);

  // Entré Tab: Core Entré fields always enabled when in Entré tab
  const entreFieldsEnabled = computed(() => isEntreTab.value);

  // Program Sync fields: Enabled based on sendLocalChanges value, regardless of active tab
  const remoteChangeIpEnabled = computed(() => sendLocalChangesIsNetwork.value);
  const remoteChangePortEnabled = computed(() => sendLocalChangesIsNetwork.value);
  const remotePhoneNumberEnabled = computed(() => sendLocalChangesIsDD.value);

  const mapApiToForm = (data) => {
    if (!data || !data.remoteOptions || !data.remoteOptions[0]) {
      return;
    }

    const apiData = data.remoteOptions[0];

    // Helper to normalize entreConnection: 'N' -> 'NETWORK', '-' -> 'NONE'
    const normalizeEntreConnection = v => {
      if (v === 'N') return 'NETWORK';
      if (v === '-' || !v) return 'NONE';
      return v;
    };

    // Helper to normalize sendLocalChanges: '-' -> 'NONE', 'N' -> 'NONE'
    const normalizeNone = v => (v === '-' || v === 'N' ? 'NONE' : v);

    // Map all fields from API to unified structure
    const std = apiData.standard || {};
    const ent = apiData.entre || {};

    remoteOptions.value = {
      // General (Standard tab)
      remoteKey: std.remoteKey || "",
      remoteDisarm: std.remoteDisarm === "Y",

      // Dialer (Standard tab)
      armedAnswerRings: parseInt(std.armedAnswerRings) || 0,
      disarmedAnswerRings: parseInt(std.disarmedAnswerRings) || 0,
      pcModem: std.pcModem === "Y",

      // Network (Standard tab)
      allowNetworkRemote: std.allowNetworkRemote === "Y",
      encryptNetworkRemote: std.encryptNetworkRemote === "Y",
      networkProgrammingPort: parseInt(std.networkProgrammingPort) || 0,

      // Cellular (Standard tab)
      allowCellularRemote: std.allowCellularRemote === "Y",
      encryptCellRemote: std.encryptCellRemote === "Y",
      cellApn1: std.cellApn1 || "",
      cellApn2: std.cellApn2 || "",

      // Entré Connection (shown in Entré tab, controls Standard payload)
      entreConnection: normalizeEntreConnection(std.entreConnection || "NONE"),
      entreInTcpPort: parseInt(std.entreInTcpPort) || 0,
      entreIpAddress: std.entreIpAddress || "0.0.0.0",
      entreArmDisarmReports: std.entreArmDisarmReports === "Y",
      entreZoneReports: std.entreZoneReports === "Y",
      entreUserCommandReports: std.entreUserCommandReports === "Y",
      entreDoorReports: std.entreDoorReports === "Y",
      entreSupervisoryReports: std.entreSupervisoryReports === "Y",

      // Entré Settings (Entré tab)
      entreOutboundTcpPort: parseInt(ent.entreOutboundTcpPort) || 0,
      entreBackupIpAddress: ent.entreBackupIpAddress || "0.0.0.0",
      entreBackupTcpPort: parseInt(ent.entreBackupTcpPort) || 0,
      entreCheckInMinutes: parseInt(ent.entreCheckInMinutes) || 0,
      entrePassphrase: ent.entrePassphrase || "",

      // Program Sync (shown in Standard tab, controls Entré payload)
      sendLocalChanges: normalizeNone(ent.sendLocalChanges || "NONE"),
      remoteChangeIp: ent.remoteChangeIp || "0.0.0.0",
      remoteChangePort: parseInt(ent.remoteChangePort) || 0,
      remotePhoneNumber: ent.remotePhoneNumber || "",

      // Shared fields
      alarmReceiver: ent.alarmReceiver || "N",
      serviceReceiver: ent.serviceReceiver || "Y",
      appKey: ent.appKey || "",
    };
  };

  const mapFormToApi = () => {
    // Format helpers
    const format2Digits = (num) => String(num).padStart(2, '0');
    const format3Digits = (num) => String(num).padStart(3, '0');
    const format5Digits = (num) => String(num).padStart(5, '0');

    let payload = {};

    // CASE 1: Standard Tab + entreConnection = NONE
    if (isStandardTab.value && entreConnectionIsNone.value) {
      payload = {
        type: "STANDARD",
        remoteKey:  remoteOptions.value.remoteKey,
        remoteDisarm: remoteOptions.value.remoteDisarm ? "Y" : "N",
        armedAnswerRings: format2Digits(remoteOptions.value.armedAnswerRings),
        disarmedAnswerRings: format2Digits(remoteOptions.value.disarmedAnswerRings),
        allowNetworkRemote: remoteOptions.value.allowNetworkRemote ? "Y" : "N",
        networkProgrammingPort: format5Digits(remoteOptions.value.networkProgrammingPort),
        encryptNetworkRemote: remoteOptions.value.encryptNetworkRemote ? "Y" : "N",
        allowCellularRemote: remoteOptions.value.allowCellularRemote ? "Y" : "N",
        cellApn1: remoteOptions.value.cellApn1,
        cellApn2: remoteOptions.value.cellApn2,
        encryptCellRemote: remoteOptions.value.encryptCellRemote ? "Y" : "N",
        entreConnection: "NONE",
        entreInTcpPort: format5Digits(remoteOptions.value.entreInTcpPort),
        entreIpAddress: remoteOptions.value.entreIpAddress,
        entreArmDisarmReports: remoteOptions.value.entreArmDisarmReports ? "Y" : "N",
        entreZoneReports: remoteOptions.value.entreZoneReports ? "Y" : "N",
        entreUserCommandReports: remoteOptions.value.entreUserCommandReports ? "Y" : "N",
        entreDoorReports: remoteOptions.value.entreDoorReports ? "Y" : "N",
        entreSupervisoryReports: remoteOptions.value.entreSupervisoryReports ? "Y" : "N"
      };
    }
    // CASE 2: Standard Tab + entreConnection = NETWORK
    else if (isStandardTab.value && entreConnectionIsNetwork.value) {
      payload = {
        type: "STANDARD",
        remoteKey:  remoteOptions.value.remoteKey,
        remoteDisarm: remoteOptions.value.remoteDisarm ? "Y" : "N",
        armedAnswerRings: format2Digits(remoteOptions.value.armedAnswerRings),
        disarmedAnswerRings: format2Digits(remoteOptions.value.disarmedAnswerRings),
        allowNetworkRemote: remoteOptions.value.allowNetworkRemote ? "Y" : "N",
        networkProgrammingPort: format5Digits(remoteOptions.value.networkProgrammingPort),
        encryptNetworkRemote: remoteOptions.value.encryptNetworkRemote ? "Y" : "N",
        allowCellularRemote: remoteOptions.value.allowCellularRemote ? "Y" : "N",
        cellApn1: remoteOptions.value.cellApn1,
        cellApn2: remoteOptions.value.cellApn2,
        encryptCellRemote: remoteOptions.value.encryptCellRemote ? "Y" : "N",
        entreConnection: "NETWORK",
        entreInTcpPort: format5Digits(remoteOptions.value.entreInTcpPort),
        entreIpAddress: remoteOptions.value.entreIpAddress,
        entreArmDisarmReports: remoteOptions.value.entreArmDisarmReports ? "Y" : "N",
        entreZoneReports: remoteOptions.value.entreZoneReports ? "Y" : "N",
        entreUserCommandReports: remoteOptions.value.entreUserCommandReports ? "Y" : "N",
        entreDoorReports: remoteOptions.value.entreDoorReports ? "Y" : "N",
        entreSupervisoryReports: remoteOptions.value.entreSupervisoryReports ? "Y" : "N"
      };
    }
    // CASE 3: Entré Tab + sendLocalChanges = NONE
    else if (isEntreTab.value && sendLocalChangesIsNone.value) {
      payload = {
        type: "ENTRE",
        entreOutboundTcpPort: format5Digits(remoteOptions.value.entreOutboundTcpPort),
        entreBackupIpAddress: remoteOptions.value.entreBackupIpAddress,
        entreBackupTcpPort: format5Digits(remoteOptions.value.entreBackupTcpPort),
        entreCheckInMinutes: format3Digits(remoteOptions.value.entreCheckInMinutes),
        entrePassphrase: remoteOptions.value.entrePassphrase,
        sendLocalChanges: "NONE",
        // Campos de Program Sync (disabled pero incluidos en payload)
        remoteChangeIp: remoteOptions.value.remoteChangeIp,
        remoteChangePort: format5Digits(remoteOptions.value.remoteChangePort),
        remotePhoneNumber: remoteOptions.value.remotePhoneNumber,
        alarmReceiver: remoteOptions.value.alarmReceiver,
        serviceReceiver: remoteOptions.value.serviceReceiver,
        appKey: remoteOptions.value.appKey
      };
    }
    // CASE 4: Entré Tab + sendLocalChanges = NETWORK
    else if (isEntreTab.value && sendLocalChangesIsNetwork.value) {
      payload = {
        type: "ENTRE",
        entreOutboundTcpPort: format5Digits(remoteOptions.value.entreOutboundTcpPort),
        entreBackupIpAddress: remoteOptions.value.entreBackupIpAddress,
        entreBackupTcpPort: format5Digits(remoteOptions.value.entreBackupTcpPort),
        entreCheckInMinutes: format3Digits(remoteOptions.value.entreCheckInMinutes),
        entrePassphrase: remoteOptions.value.entrePassphrase,
        sendLocalChanges: "NETWORK",
        remoteChangeIp: remoteOptions.value.remoteChangeIp,
        remoteChangePort: format5Digits(remoteOptions.value.remoteChangePort),
        remotePhoneNumber: remoteOptions.value.remotePhoneNumber,
        alarmReceiver: remoteOptions.value.alarmReceiver,
        serviceReceiver: remoteOptions.value.serviceReceiver,
        appKey: remoteOptions.value.appKey
      };
    }
    // CASE 5: Entré Tab + sendLocalChanges = DD
    else if (isEntreTab.value && sendLocalChangesIsDD.value) {
      payload = {
        type: "ENTRE",
        entreOutboundTcpPort: format5Digits(remoteOptions.value.entreOutboundTcpPort),
        entreBackupIpAddress: remoteOptions.value.entreBackupIpAddress,
        entreBackupTcpPort: format5Digits(remoteOptions.value.entreBackupTcpPort),
        entreCheckInMinutes: format3Digits(remoteOptions.value.entreCheckInMinutes),
        entrePassphrase: remoteOptions.value.entrePassphrase,
        sendLocalChanges: "DD",
        remoteChangeIp: remoteOptions.value.remoteChangeIp,
        remoteChangePort: format5Digits(remoteOptions.value.remoteChangePort),
        remotePhoneNumber: remoteOptions.value.remotePhoneNumber,
        alarmReceiver: remoteOptions.value.alarmReceiver,
        serviceReceiver: remoteOptions.value.serviceReceiver,
        appKey: remoteOptions.value.appKey
      };
    }

    return payload;
  };

  const loadRemoteOptions = async () => {
    try {
      const data = {
        remoteOptions: remoteOptionsData.value
      };
      mapApiToForm(data);
    } catch (error) {
      console.error("Error loading remote options:", error);
      toast({
        component: ToastificationContent,
        props: {
          title: proxy.$t("dmp.common.error"),
          text: proxy.$t("dmp.remoteOptions.messages.loadError"),
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    }
  };

  // Construye remote_options en forma GET para el template: array con un objeto
  // { entre, standard } usando códigos cortos (entreConnection 'N'/'-',
  // sendLocalChanges '-') y puertos con padding. Los originales se esparcen
  // primero para conservar llaves no modeladas.
  const buildTemplateItem = () => {
    const f = remoteOptions.value;
    const yn = (b) => (b ? "Y" : "N");
    const p2 = (n) => String(parseInt(n) || 0).padStart(2, "0");
    const p3 = (n) => String(parseInt(n) || 0).padStart(3, "0");
    const p5 = (n) => String(parseInt(n) || 0).padStart(5, "0");
    const arr = Array.isArray(remoteOptionsData.value)
      ? remoteOptionsData.value
      : [];
    const orig = arr[0] || {};

    const standard = {
      ...(orig.standard || {}),
      remoteKey: f.remoteKey,
      remoteDisarm: yn(f.remoteDisarm),
      armedAnswerRings: p2(f.armedAnswerRings),
      disarmedAnswerRings: p2(f.disarmedAnswerRings),
      pcModem: yn(f.pcModem),
      allowNetworkRemote: yn(f.allowNetworkRemote),
      encryptNetworkRemote: yn(f.encryptNetworkRemote),
      networkProgrammingPort: p5(f.networkProgrammingPort),
      allowCellularRemote: yn(f.allowCellularRemote),
      encryptCellRemote: yn(f.encryptCellRemote),
      cellApn1: f.cellApn1,
      cellApn2: f.cellApn2,
      entreConnection:
        f.entreConnection === "NETWORK"
          ? "N"
          : f.entreConnection === "NONE"
          ? "-"
          : f.entreConnection,
      entreInTcpPort: p5(f.entreInTcpPort),
      entreIpAddress: f.entreIpAddress,
      entreArmDisarmReports: yn(f.entreArmDisarmReports),
      entreZoneReports: yn(f.entreZoneReports),
      entreUserCommandReports: yn(f.entreUserCommandReports),
      entreDoorReports: yn(f.entreDoorReports),
      entreSupervisoryReports: yn(f.entreSupervisoryReports),
    };

    const origSend = (orig.entre && orig.entre.sendLocalChanges) || "";
    const entre = {
      ...(orig.entre || {}),
      entreOutboundTcpPort: p5(f.entreOutboundTcpPort),
      entreBackupIpAddress: f.entreBackupIpAddress,
      entreBackupTcpPort: p5(f.entreBackupTcpPort),
      entreCheckInMinutes: p3(f.entreCheckInMinutes),
      entrePassphrase: f.entrePassphrase,
      sendLocalChanges:
        f.sendLocalChanges === "NONE"
          ? origSend === "N"
            ? "N"
            : "-"
          : f.sendLocalChanges,
      remoteChangeIp: f.remoteChangeIp,
      remoteChangePort: p5(f.remoteChangePort),
      remotePhoneNumber: f.remotePhoneNumber,
      alarmReceiver: f.alarmReceiver,
      serviceReceiver: f.serviceReceiver,
      appKey: f.appKey,
    };

    return [{ ...orig, entre, standard }];
  };

  // Valida ambos sub-formularios (standard y entré) para el guardado del template.
  const validateTemplateSection = async () => {
    const std = standardFormObserver.value
      ? await standardFormObserver.value.validate()
      : true;
    const ent = entreFormObserver.value
      ? await entreFormObserver.value.validate()
      : true;
    return std && ent;
  };

  const saveRemoteOptions = async () => {
    const currentFormObserver = isStandardTab.value ? standardFormObserver.value : entreFormObserver.value;

    if (currentFormObserver) {
      const isValid = await currentFormObserver.validate();
      if (!isValid) {
        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.remoteOptions.messages.validationError"),
            text: proxy.$t("dmp.remoteOptions.messages.validationErrorText"),
            icon: "AlertTriangleIcon",
            variant: "warning",
          },
        });
        return;
      }
    }

    if (isTemplateMode.value) {
      // GET shape: array with a single { entre, standard } object using short
      // codes (entreConnection 'N'/'-', sendLocalChanges '-') and zero-padded
      // ports. Originals are spread first so unmodeled keys (manufacturerAuth,
      // pcModem extras, ...) survive untouched.
      const f = remoteOptions.value;
      const yn = (b) => (b ? "Y" : "N");
      const p2 = (n) => String(parseInt(n) || 0).padStart(2, "0");
      const p3 = (n) => String(parseInt(n) || 0).padStart(3, "0");
      const p5 = (n) => String(parseInt(n) || 0).padStart(5, "0");
      const arr = Array.isArray(remoteOptionsData.value)
        ? remoteOptionsData.value
        : [];
      const orig = arr[0] || {};

      const standard = {
        ...(orig.standard || {}),
        remoteKey: f.remoteKey,
        remoteDisarm: yn(f.remoteDisarm),
        armedAnswerRings: p2(f.armedAnswerRings),
        disarmedAnswerRings: p2(f.disarmedAnswerRings),
        pcModem: yn(f.pcModem),
        allowNetworkRemote: yn(f.allowNetworkRemote),
        encryptNetworkRemote: yn(f.encryptNetworkRemote),
        networkProgrammingPort: p5(f.networkProgrammingPort),
        allowCellularRemote: yn(f.allowCellularRemote),
        encryptCellRemote: yn(f.encryptCellRemote),
        cellApn1: f.cellApn1,
        cellApn2: f.cellApn2,
        entreConnection:
          f.entreConnection === "NETWORK"
            ? "N"
            : f.entreConnection === "NONE"
            ? "-"
            : f.entreConnection,
        entreInTcpPort: p5(f.entreInTcpPort),
        entreIpAddress: f.entreIpAddress,
        entreArmDisarmReports: yn(f.entreArmDisarmReports),
        entreZoneReports: yn(f.entreZoneReports),
        entreUserCommandReports: yn(f.entreUserCommandReports),
        entreDoorReports: yn(f.entreDoorReports),
        entreSupervisoryReports: yn(f.entreSupervisoryReports),
      };

      const origSend = (orig.entre && orig.entre.sendLocalChanges) || "";
      const entre = {
        ...(orig.entre || {}),
        entreOutboundTcpPort: p5(f.entreOutboundTcpPort),
        entreBackupIpAddress: f.entreBackupIpAddress,
        entreBackupTcpPort: p5(f.entreBackupTcpPort),
        entreCheckInMinutes: p3(f.entreCheckInMinutes),
        entrePassphrase: f.entrePassphrase,
        sendLocalChanges:
          f.sendLocalChanges === "NONE"
            ? origSend === "N"
              ? "N"
              : "-"
            : f.sendLocalChanges,
        remoteChangeIp: f.remoteChangeIp,
        remoteChangePort: p5(f.remoteChangePort),
        remotePhoneNumber: f.remotePhoneNumber,
        alarmReceiver: f.alarmReceiver,
        serviceReceiver: f.serviceReceiver,
        appKey: f.appKey,
      };

      emitTemplateUpdate("remote_options", [{ ...orig, entre, standard }]);
      toast({
        component: ToastificationContent,
        props: {
          title: proxy.$t("dmp.common.success"),
          text: proxy.$t("dmp.remoteOptions.messages.saveSuccess"),
          icon: "CheckIcon",
          variant: "success",
        },
      });
      return;
    }

    isSaving.value = true;

    store.commit('dmp_panels/setOperationActive', true);

    try {
      const payload = mapFormToApi();

      const response = await store.dispatch("dmp_panels/setRemoteOptions", {
        uuid: uuid.value,
        options: payload,
      });

      if (response.success) {
        emit("operation-completed");

        toast({
          component: ToastificationContent,
          props: {
            title: proxy.$t("dmp.common.success"),
            text: proxy.$t("dmp.remoteOptions.messages.saveSuccess"),
            icon: "CheckIcon",
            variant: "success",
          },
        });
      }
    } catch (error) {
      console.error("Error saving remote options:", error);
      emit("operation-error", error);
      toast({
        component: ToastificationContent,
        props: {
          title: proxy.$t("dmp.common.error"),
          text: proxy.$t("dmp.remoteOptions.messages.saveError"),
          icon: "AlertTriangleIcon",
          variant: "danger",
        },
      });
    } finally {
      store.commit('dmp_panels/setOperationActive', false);
      isSaving.value = false;
    }
  };

  onMounted(() => {
    loadRemoteOptions();
  });

  return {
    isLoading,
    isSaving,
    remoteOptions,
    yesNoOptions,
    sendLocalChangesOptions,
    entreConnectionOptions,
    activeTab,
    isStandardTab,
    isEntreTab,
    entreConnectionIsNone,
    entreConnectionIsNetwork,
    sendLocalChangesIsNone,
    sendLocalChangesIsNetwork,
    sendLocalChangesIsDD,
    standardFieldsEnabled,
    entreFieldsEnabled,
    entreConnectionFieldsEnabled,
    remoteChangeIpEnabled,
    remoteChangePortEnabled,
    remotePhoneNumberEnabled,
    standardFormObserver,
    entreFormObserver,
    loadRemoteOptions,
    saveRemoteOptions,
    isTemplateMode,
    buildTemplateItem,
    validateTemplateSection,
    t
  };
}
